import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { Configuration, Wall } from "src/types/configuration";
import useConfiguratorService from "src/hooks/useConfiguratorService";
import useConfigurationService from "src/hooks/useConfigurationService";
import useCalculationService from "src/hooks/useCalculationService";
import useRequestService from "src/hooks/useRequestService";
import useConfigurationState from "src/hooks/useConfigurationState";
import useInitialConfiguration from "src/hooks/useInitialConfiguration";
import useSynchronizeServices from "src/hooks/useSynchronizeServices";

interface ConfigurationContextProps {
  state: Configuration;
  isLoaded: boolean;
  totalPrice: string;
  uncompletedTabs: Set<number>;
  setUncompletedTabs: (uncompletedTabs: Set<number>) => void;
  outerProfileOpen: boolean;
  setOuterProfileOpen: (open: boolean) => void;
  activeWoodTab: number;
  setActiveWoodTab: (tabId: number) => void;
  activeStorageColorTab: number;
  setActiveStorageColorTab: (tabId: number) => void;
  leftStorageColorTab: number;
  setLeftStorageColorTab: (tabId: number) => void;
  rightStorageColorTab: number;
  setRightStorageColorTab: (tabId: number) => void;
  activeDoorWindowTab: number;
  setActiveDoorWindowTab: (tabId: number) => void;
  setRoofType: (roofType: string) => void;
  setDimensions: (dims: Configuration["dimensions"]) => void;
  setParapet: (parapet: Configuration["parapet"]) => void;
  setRoof: (roof: Configuration["roof"]) => void;
  setGutter: (gutter: Configuration["gutter"]) => void;
  setWalls: (walls: Configuration["walls"]) => void;
  setStorages: (storages: Configuration["storages"]) => void;
  setOptionsMaterial: (
    optionsMaterial: Configuration["optionsMaterial"]
  ) => void;
  addWall: (wall: Wall) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  configurator: any;
  configuration: any;
  calculation: any;
  request: any;
}

const ConfigurationContext = createContext<
  ConfigurationContextProps | undefined
>(undefined);

export const ConfigurationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { state, dispatch } = useConfigurationState();
  const [isInitial, setIsInitial] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [totalPrice, setTotalPrice] = useState("");
  const [uncompletedTabs, setUncompletedTabs] = useState(new Set<number>());
  const [outerProfileOpen, setOuterProfileOpen] = useState(true);
  const [activeWoodTab, setActiveWoodTab] = useState<number>(1);
  const [activeStorageColorTab, setActiveStorageColorTab] = useState<number>(1);
  const [leftStorageColorTab, setLeftStorageColorTab] = useState<number>(1);
  const [rightStorageColorTab, setRightStorageColorTab] = useState<number>(1);
  const [activeDoorWindowTab, setActiveDoorWindowTab] = useState<number>(1);
  const configurator = useConfiguratorService();
  const calculation = useCalculationService(state, configurator);
  const configuration = useConfigurationService(state, calculation);
  const request = useRequestService(configuration, configurator, calculation);

  useInitialConfiguration(isLoaded, request, (loadedConfig) => {
    dispatch({ type: "SET_CONFIGURATION", payload: loadedConfig });
  });

  const setRoofType = (roofType: string) =>
    dispatch({ type: "SET_ROOF_TYPE", payload: roofType });
  const setDimensions = (dimensions: Configuration["dimensions"]) =>
    dispatch({
      type: "SET_DIMENSIONS",
      payload: dimensions,
    });
  const setParapet = (parapet: Configuration["parapet"]) =>
    dispatch({ type: "SET_PARAPET", payload: parapet });
  const setRoof = (roof: Configuration["roof"]) =>
    dispatch({ type: "SET_ROOF", payload: roof });
  const setGutter = (gutter: Configuration["gutter"]) =>
    dispatch({ type: "SET_GUTTER", payload: gutter });
  const setWalls = (walls: Configuration["walls"]) =>
    dispatch({ type: "SET_WALLS", payload: walls });
  const setStorages = (storages: Configuration["storages"]) =>
    dispatch({ type: "SET_STORAGES", payload: storages });
  const setOptionsMaterial = (
    optionsMaterial: Configuration["optionsMaterial"]
  ) =>
    dispatch({
      type: "SET_OPTION_MATERIAL",
      payload: optionsMaterial,
    });

  const addWall = useCallback(
    (wall: Wall) => {
      const wallExists = state.walls.some(
        (w) => w.id === wall.id && w.storage === wall.storage
      );

      if (wallExists) {
        return setWalls(
          state.walls.map((w) =>
            w.id === wall.id && w.storage === wall.storage ? wall : w
          )
        );
      } else {
        return setWalls([...state.walls, wall]);
      }
    },
    [state.walls]
  );

  useSynchronizeServices(
    state,
    isInitial,
    configuration,
    calculation,
    configurator,
    setWalls,
    setIsInitial,
    setTotalPrice
  );

  return (
    <ConfigurationContext.Provider
      value={{
        state,
        totalPrice,
        isLoaded,
        uncompletedTabs,
        outerProfileOpen,
        setOuterProfileOpen,
        activeWoodTab,
        setActiveWoodTab,
        activeStorageColorTab,
        setActiveStorageColorTab,
        leftStorageColorTab,
        setLeftStorageColorTab,
        rightStorageColorTab,
        setRightStorageColorTab,
        activeDoorWindowTab,
        setActiveDoorWindowTab,
        setRoofType,
        setDimensions,
        setParapet,
        setRoof,
        setGutter,
        setWalls,
        setStorages,
        setOptionsMaterial,
        setIsLoaded,
        setUncompletedTabs,
        addWall,
        configurator,
        configuration,
        calculation,
        request,
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
};

export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error(
      "useConfiguration must be used within a ConfigurationProvider"
    );
  }
  return context;
};
